import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { terminalLayout } from '@erp/shared';
import { useTranslation } from 'react-i18next';
import { Archive, Barcode, Boxes, FolderTree, Pencil, Plus, Printer, RotateCcw, Search, Trash2 } from 'lucide-react';
import {
  MOVEMENT_REASON_LABELS, PRODUCT_TYPES, PRODUCT_TYPE_LABELS, formatMoney, formatQty, toNumber, type Paginated
} from '@erp/shared';
import { useEditLock } from '../../hooks/useEditLock';
import { EditLockBanner } from '../../components/ui/edit-lock-banner';
import { api, errorMessage, openPdf } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useDebounced } from '@/hooks/useDebounced';
import { PageHeader, SearchInput, ErrorState, EmptyState } from '@/components/ui/misc';
import { Button, buttonVariants } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { InlineView } from '@/components/ui/inline-view';
import { Field, Input, Select, Textarea } from '@/components/ui/input';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ui/use-confirm';

interface ProductRow {
  id: string; sku: string; ean13: string | null; name: string; type: string;
  family: string | null; base_unit: string; unit_kind: string; cost: number | string;
  min_stock: number | string; target_stock: number | string; physical: number | string;
  reserved: number | string; incoming: number | string;
  lead_time_days: number | string; supplier: string | null; is_active: boolean;
}

interface ProductDetail extends ProductRow {
  description: string | null; family_id: string | null; base_unit_id: string;
  purchase_unit_id: string | null; consumption_unit_id: string | null;
  purchase_to_base: string; consumption_to_base: string; cost: string;
  min_stock: string; target_stock: string; safety_stock: string;
  preferred_supplier_id: string | null; ean13: string | null;
  stocks: { location_id: string; location: string; location_name: string; warehouse: string; quantity: number }[];
  movements: { id: number; quantity: number; reason: string; location: string; created_at: string; note: string | null; user_name: string | null }[];
  suppliers: { supplier_id: string; name: string; supplier_sku: string | null; last_cost: number }[];
}

interface Family { id: string; code: string; name: string }
interface Unit { id: string; code: string; name: string; kind: string }
interface Supplier { id: string; name: string }

const EMPTY_FORM = {
  sku: '', name: '', type: 'raw_material', familyId: '', baseUnitId: '', purchaseUnitId: '',
  purchaseToBase: '1', cost: '0', minStock: '0', targetStock: '0', safetyStock: '0',
  leadTimeDays: '0', preferredSupplierId: '', ean13: '', description: '',
  isActive: true, isStorable: true
};

export function ProductsPage() {
  const { t } = useTranslation();
  const { can, user, terminal } = useAuth();
  const isAdmin = Boolean(user?.roles.some((role) => role.code === 'admin'));
  const qc = useQueryClient();
  const { confirm, dialog: dialogConfirmacion } = useConfirm();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const debounced = useDebounced(search, 300);
  const [type, setType] = useState('');
  const [lowStock, setLowStock] = useState(params.get('lowStock') === '1');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ProductRow | null>(null);
  // Los archivados se miran aparte y se pueden reactivar sin salir del listado.
  const [vista, setVista] = useState<'activos' | 'archivados'>('activos');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProductDetail | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => { setPage(1); }, [debounced, type, lowStock]);

  const query = new URLSearchParams({ page: String(page), pageSize: '20', active: vista === 'activos' ? 'true' : 'false' });
  if (debounced) query.set('q', debounced);
  if (type) query.set('type', type);
  if (lowStock) query.set('lowStock', 'true');

  const productsQuery = useQuery({
    queryKey: ['products', page, debounced, type, lowStock, vista],
    queryFn: () => api<Paginated<ProductRow>>(`/products?${query.toString()}`)
  });

  const familiesQuery = useQuery({ queryKey: ['families'], queryFn: () => api<{ data: Family[] }>('/families') });
  const unitsQuery = useQuery({ queryKey: ['units'], queryFn: () => api<{ data: Unit[] }>('/units') });
  const suppliersQuery = useQuery({
    queryKey: ['suppliers', 'options'],
    queryFn: () => api<{ data: Supplier[] }>('/suppliers/options'),
    enabled: can('suppliers.read')
  });

  const detailQuery = useQuery({
    queryKey: ['product', detailId],
    queryFn: () => api<ProductDetail>(`/products/${detailId}`),
    enabled: Boolean(detailId)
  });

  useEffect(() => {
    if (search) {
      params.set('q', search);
      setParams(params, { replace: true });
    } else {
      params.delete('q');
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const families = familiesQuery.data?.data ?? [];
  const units = unitsQuery.data?.data ?? [];
  const suppliers = suppliersQuery.data?.data ?? [];
  const rows = productsQuery.data?.data ?? [];

  const refreshAfterDelete = async () => {
    setDetailId(null);
    setSelected(null);
    await qc.invalidateQueries({ queryKey: ['products'] });
    await qc.invalidateQueries({ queryKey: ['product'] });
    await qc.invalidateQueries({ queryKey: ['stock'] });
    await qc.invalidateQueries({ queryKey: ['products-options'] });
  };

  const reactivar = async (row: ProductRow) => {
    try {
      await api(`/products/${row.id}`, { method: 'PATCH', body: { isActive: true } });
      toast.success('Artículo reactivado');
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['product'] });
      void qc.invalidateQueries({ queryKey: ['products-options'] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const archiveProduct = async (row: ProductRow) => {
    if (!isAdmin) return;
    if (!(await confirm({
      title: `Archivar ${row.sku}`,
      description: `${row.name}. Deja de aparecer en los listados activos; su historial se conserva.`,
      confirmLabel: 'Archivar'
    }))) return;
    try {
      const result = await api<{ message: string }>(`/products/${row.id}`, { method: 'DELETE' });
      toast.success(result.message || 'Artículo archivado');
      await refreshAfterDelete();
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const purgeProduct = async (row: ProductRow) => {
    if (!isAdmin) return;
    if (!(await confirm({
      title: `Eliminar definitivamente ${row.sku}`,
      description: `${row.name}. No se puede deshacer. Solo se permite si no tiene stock, BOM, kits, compras ni movimientos.`,
      confirmLabel: 'Eliminar definitivamente', destructive: true
    }))) return;
    try {
      await api(`/products/${row.id}?permanent=true`, { method: 'DELETE' });
      toast.success('Artículo eliminado definitivamente');
      await refreshAfterDelete();
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const columns = useMemo<Column<ProductRow>[]>(() => [
    {
      key: 'product',
      header: 'Artículo',
      cell: (row) => (
        <div className="min-w-[220px]">
          <p className="font-medium text-foreground">{row.name}</p>
          <p className="mt-0.5 flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
            {row.sku} {row.ean13 ? <><Barcode className="size-3" /> {row.ean13}</> : null}
          </p>
        </div>
      )
    },
    { key: 'type', header: 'Tipo', cell: (row) => <Badge tone="outline">{PRODUCT_TYPE_LABELS[row.type as keyof typeof PRODUCT_TYPE_LABELS] ?? row.type}</Badge> },
    { key: 'family', header: 'Familia', cell: (row) => <span className="text-muted-foreground">{row.family ?? '—'}</span> },
    {
      key: 'stock',
      header: 'Disponible',
      cell: (row) => {
        const available = toNumber(row.physical) - toNumber(row.reserved);
        const tone = available <= 0 ? 'danger' : available <= toNumber(row.min_stock) ? 'warning' : 'neutral';
        return (
          <div>
            <Badge tone={tone}>{formatQty(available)} {row.base_unit}</Badge>
            <p className="mt-1 text-[10px] text-muted-foreground">
              {formatQty(row.physical)} fís. · {formatQty(row.reserved)} res.
            </p>
          </div>
        );
      }
    },
    { key: 'min', header: 'Mínimo', cell: (row) => <span className="tabular-nums text-muted-foreground">{formatQty(row.min_stock)} {row.base_unit}</span> },
    { key: 'cost', header: 'Coste', cell: (row) => <span className="tabular-nums">{formatMoney(row.cost)}</span> },
    { key: 'supplier', header: 'Proveedor', cell: (row) => <span className="text-muted-foreground">{row.supplier ?? '—'}</span> },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon-sm" title="Etiqueta" onClick={(event) => { event.stopPropagation(); void openPdf(`/products/${row.id}/label.pdf?copies=1`); }}>
            <Printer />
          </Button>
          <Button variant="ghost" size="icon-sm" title="Ver ficha" onClick={(event) => {
            event.stopPropagation();
            setSelected(row);
            setDetailId(row.id);
          }}>
            <Pencil />
          </Button>
          {!row.is_active && can('products.write') ? (
            <Button variant="ghost" size="icon-sm" title="Reactivar" onClick={(event) => { event.stopPropagation(); void reactivar(row); }}>
              <RotateCcw />
            </Button>
          ) : null}
          {isAdmin ? (
            <>
              <Button variant="ghost" size="icon-sm" title="Archivar artículo (conserva historial)" hidden={!row.is_active} onClick={(event) => { event.stopPropagation(); void archiveProduct(row); }}>
                <Archive />
              </Button>
              <Button variant="ghost" size="icon-sm" title="Eliminar definitivamente (solo sin historial)" className="text-destructive hover:text-destructive" onClick={(event) => { event.stopPropagation(); void purgeProduct(row); }}>
                <Trash2 />
              </Button>
            </>
          ) : null}
        </div>
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [isAdmin, qc]);

  const totalPages = productsQuery.data?.meta.totalPages ?? 1;

  const printFilteredLabels = () => {
    const params = new URLSearchParams({ copies: '1', limit: '1000' });
    if (debounced) params.set('q', debounced);
    if (type) params.set('type', type);
    if (lowStock) params.set('lowStock', 'true');
    void openPdf(`/products/labels.pdf?${params.toString()}`)
      .catch((error) => toast.error((error as Error).message));
  };

  const printEveryProductLabel = () => {
    // Sin filtros: todos los artículos activos, estén o no almacenados.
    void openPdf('/products/labels.pdf?all=true&copies=1')
      .catch((error) => toast.error((error as Error).message));
  };

  return (
    <div>
      {dialogConfirmacion}
      <div className={formOpen || detailId ? 'hidden' : ''}>
      <PageHeader
        title="Artículos"
        description="Materias primas, componentes, consumibles, embalajes y productos fabricados"
        actions={(
          <>
            <Button onClick={printEveryProductLabel} title="Imprime el EAN-13 de todos los artículos activos, sin aplicar filtros">
              <Barcode /> Imprimir todas las etiquetas
            </Button>
            {/* Las familias tienen su propia entrada en el menú de Almacén; aquí queda el atajo,
                salvo en el armazón de puesto, donde no son una pestaña de la barra. */}
            {terminalLayout(terminal?.kind) === 'desktop' ? (
              <Link to="/families" className={buttonVariants({ variant: 'outline' })}><FolderTree /> Familias</Link>
            ) : null}
            {can('products.write') ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}><Plus /> Nuevo artículo</Button> : null}
          </>
        )}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre, SKU o EAN…" className="w-full sm:w-72" />
          <Select value={type} onChange={(event) => setType(event.target.value)} className="w-auto min-w-40">
            <option value="">Todos los tipos</option>
            {PRODUCT_TYPES.map((value) => <option key={value} value={value}>{PRODUCT_TYPE_LABELS[value]}</option>)}
          </Select>
          <Button variant={vista === 'activos' ? 'default' : 'outline'} size="sm"
            onClick={() => { setVista('activos'); setPage(1); }}>Activos</Button>
          <Button variant={vista === 'archivados' ? 'default' : 'outline'} size="sm"
            onClick={() => { setVista('archivados'); setPage(1); }}>Archivados</Button>
          <Button variant={lowStock ? 'default' : 'outline'} size="sm" onClick={() => setLowStock((value) => !value)}>
            <Boxes /> Bajo mínimo
          </Button>
          <Button variant="outline" size="sm" onClick={printFilteredLabels} title="Imprime el EAN-13 de los artículos del filtro actual">
            <Printer /> Imprimir filtro
          </Button>
          <div className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground">
            <Search className="size-3" /> {productsQuery.data?.meta.total ?? 0} artículos
          </div>
        </div>

        {productsQuery.error ? (
          <ErrorState message={(productsQuery.error as Error).message} onRetry={() => void productsQuery.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            loading={productsQuery.isLoading}
            rowKey={(row) => row.id}
            onRowClick={(row) => { setSelected(row); setDetailId(row.id); }}
            empty={<EmptyState title="Sin artículos" description={can('products.write') ? 'Crea el primer artículo o ajusta los filtros.' : 'Ajusta los filtros de búsqueda.'} action={can('products.write') ? <Button size="sm" onClick={() => setFormOpen(true)}>Nuevo artículo</Button> : undefined} />}
            footer={(
              <Pagination
                page={page}
                totalPages={totalPages}
                total={productsQuery.data?.meta.total ?? 0}
                onPageChange={setPage}
              />
            )}
          />
        )}
      </Card>
      </div>

      <ProductFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        product={editing}
        families={families}
        units={units}
        suppliers={suppliers}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['products'] });
          void qc.invalidateQueries({ queryKey: ['stock'] });
          void qc.invalidateQueries({ queryKey: ['product'] });
          setFormOpen(false);
          setEditing(null);
        }}
      />

      <ProductDetailModal
        open={Boolean(selected && detailId)}
        onOpenChange={(open) => { if (!open) { setSelected(null); setDetailId(null); } }}
        product={detailQuery.data ?? null}
        loading={detailQuery.isLoading}
        canEdit={can('products.write')}
        isAdmin={isAdmin}
        onArchive={() => { if (detailQuery.data) void archiveProduct(detailQuery.data); }}
        onPurge={() => { if (detailQuery.data) void purgeProduct(detailQuery.data); }}
        onEdit={() => {
          if (!detailQuery.data) return;
          setEditing(detailQuery.data);
          setSelected(null);
          setDetailId(null);
          setFormOpen(true);
        }}
      />
    </div>
  );
}

function ProductFormModal({ open, onOpenChange, product, families, units, suppliers, onSaved }: {
  open: boolean; onOpenChange: (open: boolean) => void; product: ProductDetail | null;
  families: Family[]; units: Unit[]; suppliers: Supplier[]; onSaved: () => void;
}) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Un artículo nuevo no existe todavía: no hay nada que bloquear hasta que se guarde.
  const lock = useEditLock('product', open && product ? product.id : null);
  const soloLectura = Boolean(product) && !lock.canEdit;

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (product) {
      setForm({
        sku: product.sku, name: product.name, type: product.type,
        familyId: product.family_id ?? '', baseUnitId: product.base_unit_id,
        purchaseUnitId: product.purchase_unit_id ?? '', purchaseToBase: product.purchase_to_base,
        cost: String(product.cost), minStock: String(product.min_stock), targetStock: String(product.target_stock),
        safetyStock: String(product.safety_stock), leadTimeDays: String(product.lead_time_days),
        preferredSupplierId: product.preferred_supplier_id ?? '', ean13: product.ean13 ?? '',
        description: product.description ?? '',
        isActive: product.is_active ?? true,
        isStorable: (product as { is_storable?: boolean }).is_storable ?? true
      });
    } else {
      setForm({ ...EMPTY_FORM, baseUnitId: units.find((u) => u.code === 'ud')?.id ?? units[0]?.id ?? '' });
    }
  }, [open, product, units]);

  const update = (key: keyof typeof form, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!form.sku.trim() || !form.name.trim() || !form.baseUnitId) {
        throw new Error('SKU, nombre y unidad base son obligatorios');
      }
      const payload = {
        sku: form.sku.trim().toUpperCase(), name: form.name.trim(), type: form.type,
        familyId: form.familyId || null, baseUnitId: form.baseUnitId,
        purchaseUnitId: form.purchaseUnitId || form.baseUnitId,
        purchaseToBase: Number(form.purchaseToBase) || 1,
        cost: Number(form.cost) || 0, minStock: Number(form.minStock) || 0,
        targetStock: Number(form.targetStock) || 0, safetyStock: Number(form.safetyStock) || 0,
        leadTimeDays: Number(form.leadTimeDays) || 0,
        preferredSupplierId: form.preferredSupplierId || null,
        ean13: form.ean13 || null, description: form.description || null,
        // Estado del artículo: sin esto no había forma de activarlo ni marcarlo almacenable.
        isActive: form.isActive ?? true, isStorable: form.isStorable ?? true
      };
      if (product) await api(`/products/${product.id}`, { method: 'PATCH', body: payload });
      else await api('/products', { method: 'POST', body: payload });
      toast.success(product ? 'Artículo actualizado' : 'Artículo creado');
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <InlineView
      open={open}
      onOpenChange={onOpenChange}
      title={product ? `Editar ${product.sku}` : 'Nuevo artículo'}
      description="Los datos maestros alimentan stock, BOM, compras y planificación."
      size="lg"
      footer={(
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button loading={saving} disabled={soloLectura} onClick={() => void submit()}>
            {product ? 'Guardar cambios' : 'Crear artículo'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        {/* Fuera del fieldset: dentro, sus botones («Continuar aquí», «Volver a intentarlo»)
            quedarían deshabilitados justo cuando hacen falta. */}
        <EditLockBanner
          state={lock.state}
          onTakeOver={() => void lock.takeOver()}
          onRetry={() => void lock.retry()}
        />
        <fieldset disabled={soloLectura} className="m-0 min-w-0 space-y-4 border-0 p-0">
        {error ? <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div> : null}
        {/* Los campos van en el orden en que se conocen al dar de alta: qué es, cómo se mide, a
            quién se le compra, cuánto se guarda y en qué estado queda. */}
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Identificación</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Tipo">
            <Select value={form.type} onChange={(e) => update('type', e.target.value)}>
              {PRODUCT_TYPES.map((value) => <option key={value} value={value}>{PRODUCT_TYPE_LABELS[value]}</option>)}
            </Select>
          </Field>
          <Field label="SKU" required><Input value={form.sku} onChange={(e) => update('sku', e.target.value)} placeholder="CRI-ESPEJO-5MM" disabled={Boolean(product)} /></Field>
          <Field label="Familia">
            <Select value={form.familyId} onChange={(e) => update('familyId', e.target.value)}>
              <option value="">Sin familia</option>
              {families.map((family) => <option key={family.id} value={family.id}>{family.name}</option>)}
            </Select>
          </Field>
          <Field label="Nombre" required className="sm:col-span-2"><Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Espejo plata 5 mm" /></Field>
          <Field label="EAN-13" hint="Se genera automáticamente si se deja vacío."><Input value={form.ean13} onChange={(e) => update('ean13', e.target.value.replace(/\D/g, '').slice(0, 13))} placeholder="8400000000017" className="font-mono" /></Field>
          <Field label="Descripción" className="sm:col-span-3"><Input value={form.description} onChange={(e) => update('description', e.target.value)} /></Field>
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Unidades</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Unidad base" required>
            <Select value={form.baseUnitId} onChange={(e) => update('baseUnitId', e.target.value)}>
              <option value="">Selecciona…</option>
              {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.code} · {unit.name}</option>)}
            </Select>
          </Field>
          <Field label="Unidad de compra">
            <Select value={form.purchaseUnitId} onChange={(e) => update('purchaseUnitId', e.target.value)}>
              <option value="">= base</option>
              {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.code}</option>)}
            </Select>
          </Field>
          <Field label="Factor compra→base" hint="Ej.: rollo = 50 m"><Input type="number" step="0.000001" value={form.purchaseToBase} onChange={(e) => update('purchaseToBase', e.target.value)} /></Field>
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Compra</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Proveedor habitual">
            <Select value={form.preferredSupplierId} onChange={(e) => update('preferredSupplierId', e.target.value)}>
              <option value="">—</option>
              {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
            </Select>
          </Field>
          <Field label="Coste (€/base)"><Input type="number" step="0.0001" value={form.cost} onChange={(e) => update('cost', e.target.value)} /></Field>
          <Field label="Lead time (días)" hint="Días desde que se pide hasta que llega"><Input type="number" min="0" value={form.leadTimeDays} onChange={(e) => update('leadTimeDays', e.target.value)} /></Field>
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Reposición</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Stock mínimo" hint="Por debajo de esto, el MRP avisa"><Input type="number" step="0.001" value={form.minStock} onChange={(e) => update('minStock', e.target.value)} /></Field>
          <Field label="Stock seguridad"><Input type="number" step="0.001" value={form.safetyStock} onChange={(e) => update('safetyStock', e.target.value)} /></Field>
          <Field label="Stock objetivo"><Input type="number" step="0.001" value={form.targetStock} onChange={(e) => update('targetStock', e.target.value)} /></Field>
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Estado</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Activo" hint="Un artículo inactivo deja de ofrecerse; el histórico se conserva">
            <label className="flex items-center gap-2 text-[12px]">
              <input type="checkbox" checked={Boolean(form.isActive)}
                onChange={(e) => update('isActive', e.target.checked)} />
              Aparece en selectores, compras y planificación
            </label>
          </Field>
          <Field label="Almacenable" hint="Solo lo almacenable entra en stock, kits y recepciones">
            <label className="flex items-center gap-2 text-[12px]">
              <input type="checkbox" checked={Boolean(form.isStorable)}
                onChange={(e) => update('isStorable', e.target.checked)} />
              Se guarda en stock
            </label>
          </Field>
        </div>
        </fieldset>
      </div>
    </InlineView>
  );
}

function ProductDetailModal({ open, onOpenChange, product, loading, canEdit, isAdmin, onEdit, onArchive, onPurge }: {
  open: boolean; onOpenChange: (open: boolean) => void; product: ProductDetail | null; loading: boolean;
  canEdit: boolean; isAdmin: boolean; onEdit: () => void; onArchive: () => void; onPurge: () => void;
}) {
  return (
    <InlineView
      open={open}
      onOpenChange={onOpenChange}
      title={product ? product.name : 'Artículo'}
      description={product ? `${product.sku} · EAN ${product.ean13 ?? '—'}` : undefined}
      size="lg"
      footer={(
        <>
          <Button variant="outline" onClick={() => product && void openPdf(`/products/${product.id}/label.pdf?copies=1`)}><Printer /> Etiqueta</Button>
          {canEdit ? <Button onClick={onEdit}><Pencil /> Editar</Button> : null}
          {isAdmin ? <Button variant="outline" onClick={onArchive}><Archive /> Archivar</Button> : null}
          {isAdmin ? <Button variant="destructive" onClick={onPurge}><Trash2 /> Eliminar definitivamente</Button> : null}
        </>
      )}
    >
      {loading || !product ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-8 animate-pulse rounded bg-muted" />)}</div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4 rounded-lg border border-border bg-muted/20 p-3 sm:grid-cols-4">
            {[
              ['Disponible', `${formatQty(toNumber(product.physical) - toNumber(product.reserved))} ${product.base_unit}`],
              ['Físico', `${formatQty(product.physical)} ${product.base_unit}`],
              ['Reservado', `${formatQty(product.reserved)} ${product.base_unit}`],
              ['Coste', formatMoney(product.cost)]
            ].map(([label, value]) => (
              <div key={label}>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
                <p className="mt-1 text-sm font-semibold tabular-nums">{value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-xs font-semibold">Reposición</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Mínimo</span><span>{formatQty(product.min_stock)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Objetivo</span><span>{formatQty(product.target_stock)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Seguridad</span><span>{formatQty(product.safety_stock)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Lead time</span><span>{product.lead_time_days} días</span></div>
              </div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-xs font-semibold">Proveedores</p>
              {product.suppliers.length === 0 ? <p className="text-xs text-muted-foreground">Sin proveedor asignado</p> : (
                <div className="space-y-1.5">
                  {product.suppliers.map((supplier) => (
                    <div key={supplier.supplier_id} className="flex items-center justify-between text-xs">
                      <span>{supplier.name}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{supplier.supplier_sku ?? '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold">Stock por ubicación</p>
            <table className="table-dense w-full rounded border border-border">
              <thead className="bg-muted/30"><tr><th>Ubicación</th><th>Almacén</th><th className="text-right">Cantidad</th></tr></thead>
              <tbody>
                {product.stocks.length === 0 ? <tr><td colSpan={3} className="text-center text-muted-foreground">Sin stock</td></tr> : product.stocks.map((stock) => (
                  <tr key={stock.location_id}><td className="font-mono text-[11px]">{stock.location}</td><td>{stock.warehouse}</td><td className="text-right tabular-nums">{formatQty(stock.quantity)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold">Últimos movimientos</p>
            <div className="max-h-56 overflow-y-auto rounded border border-border">
              <table className="table-dense w-full">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur"><tr><th>Fecha</th><th>Motivo</th><th>Ubicación</th><th className="text-right">Cantidad</th></tr></thead>
                <tbody>
                  {product.movements.length === 0 ? <tr><td colSpan={4} className="text-center text-muted-foreground">Sin movimientos</td></tr> : product.movements.map((movement) => (
                    <tr key={movement.id}>
                      <td className="text-[10px] text-muted-foreground">{new Date(movement.created_at).toLocaleString('es-ES')}</td>
                      <td><Badge tone="outline">{MOVEMENT_REASON_LABELS[movement.reason as keyof typeof MOVEMENT_REASON_LABELS] ?? movement.reason}</Badge></td>
                      <td className="font-mono text-[10px]">{movement.location}</td>
                      <td className={`text-right font-medium tabular-nums ${movement.quantity < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{movement.quantity > 0 ? '+' : ''}{formatQty(movement.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </InlineView>
  );
}
